import { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FiDownload } from 'react-icons/fi';
import { IoChevronDown } from 'react-icons/io5';
import { LuSearch } from 'react-icons/lu';
import { useProperty } from '../../../services/useProperty';
import type { ReservationTab } from '../types';

interface IReservationFiltersProps {
  activeTab: ReservationTab;
  searchValue: string;
  onSearchChange: (value: string) => void;
  bookingStatusFilter?: string;
  onBookingStatusChange?: (value: string | undefined) => void;
  propertyIdFilter?: number;
  onPropertyIdChange?: (value: number | undefined) => void;
}

const BOOKING_STATUS_OPTIONS = [
  { value: undefined, labelAr: 'الكل', labelEn: 'All' },
  { value: 'مؤكد', labelAr: 'مؤكد', labelEn: 'Confirmed' },
  { value: 'بانتظار السداد', labelAr: 'بانتظار السداد', labelEn: 'Pending Payment' },
  { value: 'معدّل', labelAr: 'معدّل', labelEn: 'Modified' },
  { value: 'ملغي', labelAr: 'ملغي', labelEn: 'Cancelled' },
];

interface FilterDropdownProps {
  label: string;
  options?: { value: string | number | undefined; label: string }[];
  selectedValue?: string | number;
  onChange?: (value: string | number | undefined) => void;
}

const FilterDropdown = ({ label, options, selectedValue, onChange }: FilterDropdownProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedLabel = selectedValue != null ? options?.find((o) => o.value === selectedValue)?.label : undefined;

  return (
    <Box ref={ref} sx={{ position: 'relative' }}>
      <Box
        onClick={() => options && options.length > 0 ? setOpen((p) => !p) : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          border: '1px solid #E5E3F8',
          borderRadius: '4px',
          px: '12px',
          py: '8px',
          cursor: 'pointer',
          backgroundColor: '#FFFFFF',
          overflow: 'hidden',
          '&:hover': { backgroundColor: '#F9F9FB' },
        }}
      >
        <IoChevronDown size={10} color="#75849F" />
        <Typography
          sx={{
            fontFamily: 'Tajawal, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            color: selectedLabel ? '#435363' : '#75849F',
            whiteSpace: 'nowrap',
          }}
        >
          {selectedLabel || label}
        </Typography>
      </Box>
      {open && options && (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E3F8',
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
            zIndex: 10,
            overflow: 'hidden',
            minWidth: '160px',
            maxHeight: '240px',
            overflowY: 'auto',
          }}
        >
          {options.map((opt, i) => (
            <Box
              key={i}
              onClick={() => {
                onChange?.(opt.value);
                setOpen(false);
              }}
              sx={{
                px: '16px',
                py: '8px',
                cursor: 'pointer',
                backgroundColor: opt.value === selectedValue ? '#F3F0FF' : 'transparent',
                '&:hover': { backgroundColor: '#F3F0FF' },
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Tajawal, sans-serif',
                  fontSize: '14px',
                  color: opt.value === selectedValue ? '#5B3FFF' : '#435363',
                  fontWeight: opt.value === selectedValue ? 600 : 400,
                  whiteSpace: 'nowrap',
                }}
              >
                {opt.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const ReservationFilters = ({
  activeTab,
  searchValue,
  onSearchChange,
  bookingStatusFilter,
  onBookingStatusChange,
  propertyIdFilter,
  onPropertyIdChange,
}: IReservationFiltersProps) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const { getManagementListQuery } = useProperty();
  const { data: propertiesData } = getManagementListQuery();

  const bookingStatusOptions = BOOKING_STATUS_OPTIONS.map((o) => ({
    value: o.value,
    label: isAr ? o.labelAr : o.labelEn,
  }));

  const propertyOptions: { value: number | undefined; label: string }[] = [
    { value: undefined, label: isAr ? 'الكل' : 'All' },
    ...((propertiesData as any[]) ?? []).map((p: any) => ({
      value: p.propertyId as number,
      label: (p.propertyName as string) ?? String(p.propertyId),
    })),
  ];

  const showPropertyFilter = activeTab !== 'vacant';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
      }}
    >
      {/* Search Input */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          border: '1px solid #E5E3F8',
          borderRadius: '4px',
          px: '12px',
          py: '8px',
          backgroundColor: '#FFFFFF',
          minWidth: '180px',
        }}
      >
        <LuSearch size={14} color="#75849F" />
        <input
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={isAr ? 'بحث' : 'Search'}
          style={{
            border: 'none',
            outline: 'none',
            fontFamily: 'Tajawal, sans-serif',
            fontSize: '14px',
            color: '#435363',
            backgroundColor: 'transparent',
            width: '100%',
            direction: isAr ? 'rtl' : 'ltr',
          }}
        />
      </Box>

      {/* Booking Status Filter */}
      <FilterDropdown
        label={t('TaskManagemnt.bookingStatus')}
        options={bookingStatusOptions}
        selectedValue={bookingStatusFilter}
        onChange={(val) => onBookingStatusChange?.(val as string | undefined)}
      />

      {/* Property Filter — hidden on vacant tab */}
      {showPropertyFilter && (
        <FilterDropdown
          label={t('TaskManagemnt.property')}
          options={propertyOptions}
          selectedValue={propertyIdFilter}
          onChange={(val) => onPropertyIdChange?.(val as number | undefined)}
        />
      )}
      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Export Button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          backgroundColor: '#5B3FFF',
          borderRadius: '8px',
          p: '8px',
          cursor: 'pointer',
          flexShrink: 0,
          '&:hover': { backgroundColor: '#4930CC' },
        }}
      >
        <FiDownload size={16} color="#FFFFFF" />
        <Typography
          sx={{
            fontFamily: 'Tajawal, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            color: '#FFFFFF',
            whiteSpace: 'nowrap',
          }}
        >
          {t('TaskManagemnt.export')}
        </Typography>
      </Box>

    </Box>
  );
};

export default ReservationFilters;
